import {
  faAngleDown,
  faAngleLeft,
  faAngleRight,
  faAngleUp,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState, useCallback, useRef } from "react";
import { FaQuestionCircle } from "react-icons/fa";
import HotelRoomCard from "./HotelRoomCard";
import { useTranslation } from "react-i18next";

export function HotelCarousel({
  filteredHotels,
  amenityIcons,
  hotelDistances,
  hotelImageIndices,
  inquiryForm,
  setInquiryForm,
  selectedRooms,
  setSelectedRooms,
  step,
  openModal,
  currentHotelIndex,
  setCurrentHotelIndex,
}) {
  const [openHotelModal, setOpenHotelModal] = useState(false);
  const hotelModalRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        hotelModalRef.current &&
        !hotelModalRef.current.contains(event.target)
      ) {
        handleCloseModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOpenModal = () => {
    setOpenHotelModal(true);
  };

  const handleCloseModal = () => {
    setOpenHotelModal(false);
  };

  const handlePrev = () => {
    setCurrentHotelIndex((prev) =>
      prev === 0 ? filteredHotels.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentHotelIndex((prev) =>
      prev === filteredHotels.length - 1 ? 0 : prev + 1
    );
  };
  const hotel = filteredHotels[currentHotelIndex];

  const autoSelectRoomsForGuests = useCallback(
    (roomTypes, guestCount) => {
      if (!roomTypes || guestCount <= 0) return [];

      const sortedRooms = [...roomTypes].sort(
        (a, b) => b.capacity - a.capacity
      );
      const selected = [];
      let remainingGuests = guestCount;

      for (const room of sortedRooms) {
        while (remainingGuests >= room.capacity) {
          selected.push(room);
          remainingGuests -= room.capacity;
        }
      }
      if (remainingGuests > 0) {
        const smallestFit = [...roomTypes]
          .sort((a, b) => a.capacity - b.capacity) // Low → High
          .find((room) => room.capacity >= remainingGuests);

        if (smallestFit) {
          selected.push(smallestFit);
        }
      }
      setInquiryForm((prev) => ({
        ...prev,
        hotelPreference: selected.map((room) => {
          return {
            hotelId: hotel._id,
            roomType: room.type,
            hotelName: hotel.name,
            pricePerNight: room.pricePerNight,
            roomId: room._id,
          };
        }),
      }));
      return selected;
    },
    [hotel, setInquiryForm]
  );

  useEffect(() => {
    if (
      hotel?.roomTypes?.length > 0 &&
      inquiryForm.numberOfGuests > 0 &&
      step === 3
    ) {
      const rooms = autoSelectRoomsForGuests(
        hotel.roomTypes,
        inquiryForm.numberOfGuests
      );
      setSelectedRooms(rooms);
    }
  }, [
    hotel,
    inquiryForm.numberOfGuests,
    autoSelectRoomsForGuests,
    setSelectedRooms,
    step,
  ]);

  if (!filteredHotels.length) return <p>No hotels found.</p>;

  const validAmenities = hotel.amenities.filter(
    (amenity) => !!amenityIcons[amenity]
  );
  const displayedAmenities = validAmenities.slice(0, 5);
  const allImages = [
    ...(hotel.images || []),
    ...hotel.roomTypes.flatMap((rt) => rt.images || []),
  ];
  const currentImageIndex = hotelImageIndices[hotel._id] ?? 0;
  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[...Array(rating)].map((_, i) => (
          <span key={i} className={"text-yellow-500"}>
            ★
          </span>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="w-full relative p-2">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
          <h1 className="text-xl h-14">
            {t("EventPage.ourHandpickedHotelsIn")} {hotel?.city ?? ""}
          </h1>
          {!openHotelModal ? (
            <div
              className="border border-gray-300 rounded-md px-2 py-1 md:w-[320px] flex justify-between items-center"
              onClick={openHotelModal ? handleCloseModal : handleOpenModal}
            >
              <div className="flex items-center gap-2">
                <img
                  src="/images/hotel.jpg"
                  alt="Hotel"
                  className="w-4 h-4 lg:w-7 lg:h-7 object-cover rounded"
                />
                <div>
                  <h6>{hotel.name}</h6>
                  <p className="font-semibold text-sm">
                    {inquiryForm.numberOfGuests} {t("EventPage.Guests")} |{" "}
                    {selectedRooms.length} {t("EventPage.room")}
                  </p>
                </div>
              </div>
              {!openHotelModal && (
                <div className="pr-3 cursor-pointer">
                  <FontAwesomeIcon icon={faAngleDown} />
                </div>
              )}
              {openHotelModal && (
                <div className="pr-3 cursor-pointer">
                  <FontAwesomeIcon icon={faAngleUp} />
                </div>
              )}
            </div>
          ) : (
            <div className="w-[320px] h-[54px]"></div>
          )}
        </div>
        {openHotelModal && (
          <HotelRoomCard
            handleClose={handleCloseModal}
            filteredHotels={filteredHotels}
            hotel={hotel}
            inquiryForm={inquiryForm}
            setInquiryForm={setInquiryForm}
            setCurrentHotelIndex={setCurrentHotelIndex}
            selectedRooms={selectedRooms}
            setSelectedRooms={setSelectedRooms}
            setOpenHotelModal={setOpenHotelModal}
            hotelModalRef={hotelModalRef}
          />
        )}

        {/* Hotel Info */}
        <div className="sm:flex justify-between items-end">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-md">{hotel.name}</h3>
              <div>{renderStars(hotel.starRating)}</div>
            </div>
            <div className="sm:flex gap-2 mb-2">
              <p className="text-gray-500 text-sm">{hotel.address}</p>
              <p className="text-gray-500 text-sm">
                ( {t("EventPage.distanceToVenue")}:{" "}
                {hotelDistances[hotel._id] || t("EventPage.calculating")})
              </p>
            </div>
          </div>
          <div className="flex gap-2 mb-2">
            {displayedAmenities.map((amenity, index) => {
              const iconUrl = amenityIcons[amenity];
              return iconUrl ? (
                <img
                  key={index}
                  src={iconUrl}
                  alt={amenity}
                  className="w-5 h-5 object-contain grayscale"
                  style={{ minWidth: 25, minHeight: 25 }}
                />
              ) : (
                <FaQuestionCircle key={index} className="text-black text-xl" />
              );
            })}
          </div>
        </div>

        <div className="mt-2 relative">
          {/* Prev/Next hotel navigation arrows - positioned on the image only */}
          <button
            onClick={handlePrev}
            aria-label={t("EventPage.previousHotel")}
            className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-950 flex items-center justify-center hover:bg-gray-800 transition-colors"
          >
            <FontAwesomeIcon icon={faAngleLeft} className="text-white" />
          </button>

          <button
            onClick={handleNext}
            aria-label={t("EventPage.nextHotel")}
            className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 z-10 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-950 flex items-center justify-center hover:bg-gray-800 transition-colors"
          >
            <FontAwesomeIcon icon={faAngleRight} className="text-white" />
          </button>

          {allImages[currentImageIndex] && (
            <img
              src={allImages[currentImageIndex]}
              alt={hotel.name}
              className="rounded-md w-full lg:h-[250px]"
              onClick={() => openModal(allImages[currentImageIndex])}
            />
          )}
          {!allImages[currentImageIndex] && (
            <div className="h-[80px] md:h-[200px] lg:h-[250px]"></div>
          )}
        </div>

        <div
          className="text-gray-800 mt-4 leading-relaxed whitespace-pre-line"
          dangerouslySetInnerHTML={{ __html: hotel.description }}
        />
        {/* <div className="flex justify-end">
          <button
            className="bg-opera-red text-white px-3 py-1 rounded-md hover:bg-red-700"
            onClick={openShowMore ? handleShowMoreClose : handleShowMoreOpen}
          >
            {openShowMore ? "Less Info" : "More Info"}
          </button>
        </div>
        {openShowMore && (
          <div className="mt-5">
            <h2>Rooms</h2>
            {hotel?.roomTypes?.map((room, index) => (
              <div key={index}>
                {
                  <div className="mb-5">
                    <div className="flex justify-between">
                      <h4>{room.type}</h4>
                      <div className="flex gap-2">
                        {displayedAmenities.map((amenity, index) => {
                          const Icon = amenityIcons[amenity];
                          return Icon ? (
                            <Icon key={index} className="text-black text-xl" />
                          ) : (
                            <FaQuestionCircle
                              key={index}
                              className="text-black text-xl"
                            />
                          );
                        })}
                      </div>
                    </div>
                    {room.images[0] && <img src={room.images[0]} alt="" />}
                    <p>{room.description}</p>
                  </div>
                }
              </div>
            ))}
          </div>
        )} */}
      </div>
    </>
  );
}
